import {
    ShoppingBagIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    UserIcon,
    GlobeAltIcon,
} from "@heroicons/react/24/solid";
import { MemoTextJsonTypeEnum } from "@/entities/MemoText/MemoText.model";
import { formattedUrl, money } from "@/utils/misc";
import {
    NAICS_6_DIGIT_CODE,
    NAICS_INDUSTRY_GROUP_OPTIONS,
} from "@/constants/industries.constants";
import clsx from "clsx";
import { useColorizedValue, DataSource } from "../hooks/useColorizedValue";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { US_STATES } from "@/constants/location.constants";
import type { Company } from "@/entities/Company";
import type { SfdcData } from "@/entities/Deal/SFDC.types";

const InfoDisplay = ({
    value,
    isLoading,
    valueClassName,
    source,
    sourceClassName,
    showDataSources,
}: {
    value?: string | number;
    isLoading: boolean;
    valueClassName?: string;
    source?: string | null;
    sourceClassName?: string;
    showDataSources?: boolean;
}) => {
    if (isLoading) {
        return (
            <div
                className={clsx(
                    "h-3 w-10 bg-slate-700",
                    "animate-pulse rounded",
                )}
            />
        );
    }

    return (
        <div className="flex flex-col">
            <p className={clsx("body-s subdued", valueClassName)}>
                {value || "-"}
            </p>
            {showDataSources && source && (
                <span
                    className={clsx(
                        "text-[9px] font-medium tracking-wide",
                        sourceClassName,
                    )}
                >
                    {source}
                </span>
            )}
        </div>
    );
};

export const MetaInfoBar = ({
    company,
    sfdcData,
    text = {},
    isGenerating,
    isLoadingCompany = false,
    isLoadingSfdc = false,
    hideDataSources = false,
}: {
    company: Company | undefined;
    sfdcData: SfdcData[] | undefined;
    text?: Record<string, any>;
    isGenerating: boolean;
    isLoadingCompany?: boolean;
    isLoadingSfdc?: boolean;
    hideDataSources?: boolean;
}) => {
    const flags = useFlags();
    const showDataSources =
        !hideDataSources && flags.enabled("show-data-sources");

    const facilitySize = sfdcData?.[0]?.totalFacilitySize;
    const metadata = text?.[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data;
    const companyState = company?.state ? US_STATES[company?.state] : null;
    const companyCountry = company?.country;
    const locationValue = [companyState, companyCountry]
        .filter(Boolean)
        .join(", ");

    const facilitySizeColorized = useColorizedValue(
        facilitySize ? money(facilitySize) : undefined,
        DataSource.SFDC,
        "sfdc.totalFacilitySize",
    );
    const locationColorized = useColorizedValue(
        locationValue || undefined,
        DataSource.LEGACY_BACKEND,
        "company.state, company.country",
    );
    const industryColorized = useColorizedValue(
        NAICS_6_DIGIT_CODE[metadata?.naicsCode as string] ||
            NAICS_INDUSTRY_GROUP_OPTIONS[
                company?.capitalApplication.selfReportedIndustry as string
            ],
        metadata?.naicsCode ? DataSource.MEMO_TEXT : DataSource.LEGACY_BACKEND,
        metadata?.naicsCode
            ? "memoText.companyMetadata.naicsCode"
            : "company.capitalApplication.selfReportedIndustry",
    );
    const headcountColorized = useColorizedValue(
        metadata?.headcountEstimate,
        DataSource.MEMO_TEXT,
        "memoText.companyMetadata.headcountEstimate",
    );
    const websiteColorized = useColorizedValue(
        formattedUrl(company?.website || null),
        DataSource.LEGACY_BACKEND,
        "company.website",
    );

    const stageName = sfdcData?.[0]?.stage;

    return (
        <div className="flex flex-wrap gap-6">
            {stageName && (
                <div className="flex items-center">
                    <div
                        className={clsx(
                            "max-h-[24px] w-fit rounded-full bg-[#93C5FD] px-2 py-1",
                            "flex items-center",
                        )}
                    >
                        <span className="ui-label text-slate-900">
                            {stageName}
                        </span>
                    </div>
                </div>
            )}
            <div className="flex items-center gap-1.5">
                <CurrencyDollarIcon className=" size-4" />
                <InfoDisplay
                    value={facilitySizeColorized.value}
                    valueClassName={
                        hideDataSources ? "" : facilitySizeColorized.className
                    }
                    source={facilitySizeColorized.source}
                    sourceClassName={facilitySizeColorized.sourceClassName}
                    showDataSources={showDataSources}
                    isLoading={isLoadingSfdc}
                />
            </div>

            <div className="flex items-center gap-1.5">
                <MapPinIcon className=" size-4" />
                <InfoDisplay
                    value={locationColorized.value}
                    valueClassName={
                        hideDataSources ? "" : locationColorized.className
                    }
                    source={locationColorized.source}
                    sourceClassName={locationColorized.sourceClassName}
                    showDataSources={showDataSources}
                    isLoading={isLoadingCompany}
                />
            </div>

            <div className="flex items-center gap-1.5">
                <ShoppingBagIcon className=" size-4" />
                <InfoDisplay
                    value={industryColorized.value}
                    valueClassName={
                        hideDataSources ? "" : industryColorized.className
                    }
                    source={industryColorized.source}
                    sourceClassName={industryColorized.sourceClassName}
                    showDataSources={showDataSources}
                    isLoading={isLoadingCompany || isGenerating}
                />
            </div>

            <div className="flex items-center gap-1.5">
                <UserIcon className=" size-4" />
                <InfoDisplay
                    value={headcountColorized.value}
                    valueClassName={
                        hideDataSources ? "" : headcountColorized.className
                    }
                    source={headcountColorized.source}
                    sourceClassName={headcountColorized.sourceClassName}
                    showDataSources={showDataSources}
                    isLoading={isGenerating}
                />
            </div>
            <div className="flex items-center gap-1.5">
                <GlobeAltIcon className=" size-4" />
                <InfoDisplay
                    value={websiteColorized.value}
                    valueClassName={
                        hideDataSources ? "" : websiteColorized.className
                    }
                    source={websiteColorized.source}
                    sourceClassName={websiteColorized.sourceClassName}
                    showDataSources={showDataSources}
                    isLoading={isLoadingCompany}
                />
            </div>
        </div>
    );
};
